import {
  AlertTriangle,
  BookOpen,
  Clock,
  Flame,
  type LucideIcon,
  TrendingUp,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

/**
 * OKS-472 — MissionCard urgency variants (Figma `164:695`, 5 chip symbols).
 *
 * Superset of the server-emitted `CollabUrgency` (`default` / `due_today` /
 * `overdue`): the component API also supports `story` + `trending`, which are
 * NOT wired from `Mission#urgency` in V1 (TRD KD-6) — they exist so the
 * design-system card is complete and OKS-473+ can drive them once the
 * backend computes those signals.
 */
export type MissionCardUrgency =
  | "default"
  | "due_today"
  | "overdue"
  | "story"
  | "trending"

interface UrgencyVariantConfig {
  icon: LucideIcon
  /** Overlay chip colour. Paired with a distinct ICON + TEXT so the signal is
   *  never colour-only (a11y, UX Spec §6). Palette from UX Spec §2.5. */
  chipClass: string
  i18nKey: string
}

// i18n keys resolved via `t(config.i18nKey)` — declared explicitly so the
// static-AST scanner sees each as live:
// inertia-i18n-use missions.card.urgency.default missions.card.urgency.due_today missions.card.urgency.overdue missions.card.urgency.story missions.card.urgency.trending
const VARIANTS: Record<MissionCardUrgency, UrgencyVariantConfig> = {
  default: {
    icon: Clock,
    chipClass: "bg-foreground/80 text-background",
    i18nKey: "missions.card.urgency.default",
  },
  due_today: {
    icon: Flame,
    chipClass: "bg-[#ff5c00] text-white",
    i18nKey: "missions.card.urgency.due_today",
  },
  overdue: {
    icon: AlertTriangle,
    chipClass: "bg-[#ef4444] text-white",
    i18nKey: "missions.card.urgency.overdue",
  },
  story: {
    icon: BookOpen,
    chipClass: "bg-[#3b82f6] text-white",
    i18nKey: "missions.card.urgency.story",
  },
  trending: {
    icon: TrendingUp,
    chipClass: "bg-[#ff5c00] text-white",
    i18nKey: "missions.card.urgency.trending",
  },
}

interface UrgencyChipProps {
  urgency: MissionCardUrgency
  className?: string
}

/**
 * OKS-472 — Overlaid urgency chip for the MissionCard cover (top-left). Icon +
 * colour + text — never colour alone. `default` renders a neutral dark chip;
 * the other four use the accent palette from UX Spec §2.5.
 */
export function UrgencyChip({ urgency, className }: UrgencyChipProps) {
  const { t } = useTranslation()
  const { icon: Icon, chipClass, i18nKey } = VARIANTS[urgency]

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold shadow-sm",
        chipClass,
        className,
      )}
      data-testid="mission-card-urgency-chip"
      data-urgency={urgency}
    >
      <Icon className="size-3" aria-hidden="true" />
      {t(i18nKey)}
    </span>
  )
}
